import { TourPlayback } from "@common/schemas";
import { QuestionChoice } from "@common/schemas/v2";
import * as schemas from "@common/schemas";

export type FlowFinishHookType = "loadCheckpoint" | "finishTour";
export type FlowHookType = FlowFinishHookType;

export type FlowHooks = {
  onFinish?: FlowFinishHookType;
};

export type FlowOptions = {
  isAvatarProgressRingVisible: boolean;
};

export const systemFlowTypes = ["unsuspend", "targetNotFound", "wanderingOff"] as const;
export type SystemFlowType = (typeof systemFlowTypes)[number];
export type SystemFlows = Record<SystemFlowType, Flow>;
export type FlowKey = SystemFlowType | "tour";

type Flow = {
  key: FlowKey;
  sections: TourPlayback["sections"];
  hooks?: FlowHooks;
  options: FlowOptions;
};

export interface UnsuspendFlowParams {
  resumeTour: schemas.Fillers["resumeTour"];
}

export interface WanderingOffFlowParams {
  targetLost: schemas.Fillers["targetLost"];
  resumeTour: schemas.Fillers["resumeTour"];
}

export interface TargetNotFoundFlowParams {
  targetNotFound: schemas.Fillers["targetNotFound"];
}

export interface CreateSystemFlowsParams {
  unsuspendFlow: UnsuspendFlowParams;
  targetNotFoundFlow: TargetNotFoundFlowParams;
  wanderingOffFlow: WanderingOffFlowParams;
}

export const createFlows = (
  tourPlayback: TourPlayback,
  systemFlowParams: CreateSystemFlowsParams
): Record<FlowKey, Flow> => {
  return {
    tour: createTourFlow(tourPlayback),
    ...createSystemFlows(systemFlowParams),
  };
};

export const createSystemFlows = ({
  unsuspendFlow,
  targetNotFoundFlow,
  wanderingOffFlow,
}: CreateSystemFlowsParams): SystemFlows => {
  return {
    unsuspend: createUnsuspendFlow(unsuspendFlow),
    targetNotFound: createTargetNotFoundFlow(targetNotFoundFlow),
    wanderingOff: buildWanderingOffFlow({
      targetLost: wanderingOffFlow.targetLost,
      resumeTour: wanderingOffFlow.resumeTour,
    }),
  };
};

export const createTourFlow = (tourPlayback: TourPlayback): Flow => {
  return {
    key: "tour",
    sections: tourPlayback.sections,
    hooks: { onFinish: "finishTour" },
    options: {
      isAvatarProgressRingVisible: true,
    },
  };
};

const createUnsuspendFlow = (params: UnsuspendFlowParams): Flow => {
  return {
    key: "unsuspend",
    sections: [
      {
        id: "unsuspend",
        mediaDuration: params.resumeTour!.mediaDuration!,
        avatarMediaUrl: params.resumeTour!.videoUrl!,
        text: "",
        transcript: [],
        steps: [
          {
            id: "resumeTour",
            start: 0,
            end: params.resumeTour!.mediaDuration!,
            text: "",
            attributes: {
              isCheckpoint: false,
              type: "filler",
            },
          },
        ],
      },
    ],
    hooks: {
      onFinish: "loadCheckpoint",
    },
    options: {
      isAvatarProgressRingVisible: false,
    },
  };
};

const createTargetNotFoundFlow = (params: TargetNotFoundFlowParams): Flow => {
  const targetNotFoundQuestionChoices: QuestionChoice[] = [
    {
      id: "restart",
      type: "proceedNext",
      text: params.targetNotFound!.restartButtonText!,
    },
  ];

  if (params.targetNotFound!.contactSupportUrl) {
    targetNotFoundQuestionChoices.push({
      id: "contactSupport",
      type: "contactSupport",
      text: params.targetNotFound!.contactSupportButtonText!,
      url: params.targetNotFound!.contactSupportUrl,
    });
  }

  return {
    key: "targetNotFound",
    sections: [
      {
        id: "targetNotFound",
        mediaDuration: params.targetNotFound!.mediaDuration!,
        avatarMediaUrl: params.targetNotFound!.videoUrl!,
        text: "",
        transcript: [],
        steps: [
          {
            id: "targetNotFound",
            text: "",
            start: 0,
            end: params.targetNotFound!.mediaDuration!,
            attributes: {
              isCheckpoint: false,
              type: "question",
              questionType: "multipleChoice",
              choices: targetNotFoundQuestionChoices,
              title: params.targetNotFound!.title!,
              text: params.targetNotFound!.content!,
            },
          },
        ],
      },
    ],
    hooks: {
      onFinish: "loadCheckpoint",
    },
    options: {
      isAvatarProgressRingVisible: false,
    },
  };
};

const buildWanderingOffFlow = (params: WanderingOffFlowParams): Flow => {
  return {
    key: "wanderingOff",
    sections: [
      {
        id: "wanderingOff",
        mediaDuration: params.targetLost!.mediaDuration!,
        avatarMediaUrl: params.targetLost!.videoUrl!,
        text: "",
        transcript: [],
        steps: [
          {
            id: "wanderingOffQuestion",
            text: "",
            start: 0,
            end: params.targetLost!.mediaDuration!,
            attributes: {
              isCheckpoint: false,
              type: "question",
              questionType: "multipleChoice",
              choices: [
                {
                  id: "resume",
                  type: "proceedNext",
                  text: params.targetLost!.resumeTourButtonText!,
                },
                {
                  id: "later",
                  type: "suspendTour",
                  text: params.targetLost!.laterButtonText!,
                },
              ],
              title: params.targetLost!.title!,
              text: params.targetLost!.content!,
            },
          },
        ],
      },
      {
        id: "resumeTour",
        mediaDuration: params.resumeTour!.mediaDuration!,
        avatarMediaUrl: params.resumeTour!.videoUrl!,
        text: "",
        transcript: [],
        steps: [
          {
            id: "resumeTour",
            start: 0,
            end: params.resumeTour!.mediaDuration!,
            text: "",
            attributes: {
              isCheckpoint: false,
              type: "filler",
            },
          },
        ],
      },
    ],
    hooks: {
      onFinish: "loadCheckpoint",
    },
    options: {
      isAvatarProgressRingVisible: false,
    },
  };
};
